import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, ChevronDown, CheckCircle, X, AlertCircle, FileText,
  Clock, Truck, Package, Calendar, Tag, Shield, Loader, LayoutGrid, List, Clipboard, ArrowLeft,
  CheckSquare, AlertTriangle, Edit, Eye, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../../../context/AuthContext';
import inboundTruckService from '../services/inboundTruckService';
import { useTimezone } from '../../../../../hooks/useTimezone';
import BusinessUnitName from '../../components/BusinessUnitName';
import TruckTile from '../../components/TruckTile';
import businessUnitService from '../../../../../services/businessUnitService';

/**
 * ReceivingView component for the inbound truck scheduler module
 * Displays trucks that need to be received and allows users to progress them through the receiving workflow
 */
const ReceivingView = ({ truckData, setTruckData, refreshData, isLoading, socket, isConnected, openTruckEditor }) => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'list'
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  
  // Use inbound truck service
  const truckService = inboundTruckService;
  
  // Form state
  const [formData, setFormData] = useState({
    arrived_at: '',
    conforming: true,
    receiver_notes: '',
  });
  
  // Loading and error states
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Live time tracking
  const [useLiveTime, setUseLiveTime] = useState(true);
  const intervalRef = useRef(null);
  
  // Putaway confirmation dialog
  const [putawayConfirmOpen, setPutawayConfirmOpen] = useState(false);
  
  // Get auth context
  const { currentUser } = useAuth();
  
  // API URL
  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // Action labels for this view
  const actionLabels = {
    receiving: 'Receiving',
    putaway: 'Putaway'
  };

  // Load business units on component mount
  useEffect(() => {
    const loadBusinessUnits = async () => {
      try {
        const units = await businessUnitService.getAllBusinessUnits();
        setBusinessUnits(units);
      } catch (error) {
        console.error('Error loading business units:', error);
      }
    };
    loadBusinessUnits();
  }, []);

  // Handle status transitions
  const handleStatusChange = async (truckId, newStatus, additionalData = {}) => {
    try {
      setLocalLoading(true);
      setError(null);

      let updatedTruck;

      // Use the appropriate method based on the status transition
      switch (newStatus) {
        case 'Arrived':
          updatedTruck = await truckService.markTruckAsArrived(truckId, {
            arrived_at: new Date().toISOString()
          });
          break;
        case 'Received':
          updatedTruck = await truckService.startReceiving(truckId);
          break;
        case 'Putaway':
          // Use conforming and receiver_notes from additionalData if provided
          console.log('Preparing to complete receiving with data:', additionalData);
          const receivingData = {
            conforming: additionalData.conforming !== undefined ? additionalData.conforming : true,
            receiver_notes: additionalData.receiver_notes || ''
          };
          console.log('Complete receiving payload:', receivingData);

          updatedTruck = await truckService.completeReceiving(truckId, receivingData);
          console.log('Complete receiving response:', updatedTruck);
          break;
        case 'Completed':
          // This is putaway completion for inbound
          updatedTruck = await truckService.updateTruckStatus(truckId, 'Completed', {
            completed_at: new Date().toISOString()
          });
          break;
        case 'Cancelled':
          updatedTruck = await truckService.updateTruckStatus(truckId, 'Cancelled', {
            cancelled_at: new Date().toISOString()
          });
          break;
        default:
          // Generic status update
          updatedTruck = await truckService.updateTruckStatus(truckId, newStatus);
      }

      // Update the truckData with the updated truck
      if (updatedTruck && updatedTruck.truck) {
        const updatedTruckData = truckData.map(truck =>
          truck.id === truckId ? updatedTruck.truck : truck
        );
        setTruckData(updatedTruckData);
      } else {
        // If no specific truck returned, refresh all data
        refreshData();
      }

      setLocalLoading(false);
    } catch (error) {
      console.error('Error changing truck status:', error);

      // Enhanced error handling to extract more detailed error information
      let errorMessage = 'Error updating truck status';

      if (typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        }

        if (error.details && Object.keys(error.details).length > 0) {
          // If there are field-specific errors, show them
          const fieldErrors = [];
          for (const [field, message] of Object.entries(error.details)) {
            if (field !== 'message') {
              fieldErrors.push(`${field}: ${message}`);
            }
          }

          if (fieldErrors.length > 0) {
            errorMessage += ` (${fieldErrors.join(', ')})`;
          }
        }

        if (error.status) {
          errorMessage += ` [Status: ${error.status}]`;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Set the error message
      setError(errorMessage);
      setLocalLoading(false);
    }
  };

  // Create stable refs for form values to prevent losing focus
  const formValueRef = useRef({
    receiver_notes: '',
    conforming: true,
    arrived_at: ''
  });
  
  // Create a ref for our isolated notes field component
  const notesFieldRef = useRef(null);

  // Filter trucks for receiving view
  const receivingTrucks = truckData.filter(truck => 
    truck.status !== 'Completed' && 
    (truck.status === 'Scheduled' || 
     truck.status === 'Arrived' || 
     truck.status === 'Received' || 
     truck.status === 'Putaway')
  );

  // Helper function for robust/fuzzy string matching
  const fuzzyMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return false;
    
    // Convert to lowercase for case-insensitive matching
    const normalizedText = text.toString().toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();
    
    // Direct substring match
    if (normalizedText.includes(normalizedSearch)) return true;
    
    // Check if all characters of search term appear in order in the text
    let searchIndex = 0;
    for (let i = 0; i < normalizedText.length && searchIndex < normalizedSearch.length; i++) {
      if (normalizedText[i] === normalizedSearch[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === normalizedSearch.length;
  };

  // Filter and sort trucks
  const filteredTrucks = receivingTrucks.filter(truck => {
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'scheduled' && truck.status !== 'Scheduled') return false;
      if (statusFilter === 'arrived' && truck.status !== 'Arrived') return false;
      if (statusFilter === 'in-progress' && truck.status !== 'Received') return false;
      if (statusFilter === 'completed' && truck.status !== 'Completed') return false;
      if (statusFilter === 'putaway-ready' && truck.status !== 'Putaway') return false;
    }
    
    // Apply search filter — match the same field set the server's search param covers.
    if (searchTerm) {
      const businessUnit = businessUnits.find(unit =>
        String(unit.id) === String(truck.business_unit)
      );
      const fields = [
        truck.carrier,
        truck.po,
        truck.conf_number,
        truck.vendor,
        truck.scheduler_notes,
        truck.receiver_notes,
        businessUnit && businessUnit.name,
      ];
      if (!fields.some((f) => f && fuzzyMatch(String(f), searchTerm))) {
        return false;
      }
    }

    return true;
  });

  // Sort trucks by scheduled date
  const sortedTrucks = [...filteredTrucks].sort((a, b) => 
    new Date(a.scheduled_at) - new Date(b.scheduled_at)
  );

  // Helper function to group trucks by date within scheduled status
  const groupScheduledByDate = (trucks) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const grouped = {};
    
    trucks.forEach(truck => {
      const scheduledDate = new Date(truck.scheduled_at);
      const dateKey = scheduledDate.toDateString();
      
      // Check if it's today
      const isToday = scheduledDate.toDateString() === today.toDateString();
      const displayKey = isToday ? 'Today' : dateKey;
      
      if (!grouped[displayKey]) {
        grouped[displayKey] = {
          date: scheduledDate,
          trucks: [],
          isToday: isToday
        };
      }
      
      grouped[displayKey].trucks.push(truck);
    });
    
    // Sort the groups by date
    const sortedGroups = Object.entries(grouped).sort((a, b) => {
      // Today always comes first
      if (a[1].isToday) return -1;
      if (b[1].isToday) return 1;
      // Then sort by date
      return a[1].date - b[1].date;
    });
    
    return sortedGroups;
  };

  // Group trucks by status for board view
  const groupedTrucks = {
    scheduled: [],
    arrived: [],
    inProgress: [],
    completed: []
  };

  // Group trucks by status
  sortedTrucks.forEach(truck => {
    if (truck.status === 'Completed') {
      // Don't show completed trucks
      return;
    } else if (truck.status === 'Putaway') {
      groupedTrucks.completed.push(truck);
    } else if (truck.status === 'Received') {
      groupedTrucks.inProgress.push(truck);
    } else if (truck.status === 'Arrived') {
      groupedTrucks.arrived.push(truck);
    } else {
      // Default is Scheduled
      groupedTrucks.scheduled.push(truck);
    }
  });
  
  // Group scheduled trucks by date
  const scheduledByDate = groupScheduledByDate(groupedTrucks.scheduled);

  // Empty state when no trucks match filters
  const EmptyState = () => (
    <div className="bg-slate-750 rounded-lg p-8 text-center h-full flex flex-col items-center justify-center">
      <Truck className="w-16 h-16 text-slate-600 mb-3" />
      <p className="text-slate-400 text-lg mb-2">No trucks match your current filters</p>
      <p className="text-slate-500 mb-4">Try adjusting your search criteria or filters</p>
      {(statusFilter !== 'all' || searchTerm) && (
        <button 
          className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
          onClick={() => {
            setStatusFilter('all');
            setSearchTerm('');
          }}
        >
          Clear Filters
        </button>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Action bar - search, filters and view toggle */}
      <div className="px-4 py-3 bg-slate-800 rounded-lg mx-4 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search carrier, vendor, PO, conf #, business unit, notes…"
            className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="bg-slate-700 rounded-lg flex">
            <button 
              className={`p-2 ${viewMode === 'list' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'} rounded-l-lg`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={16} />
            </button>
            <button 
              className={`p-2 ${viewMode === 'board' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'} rounded-r-lg`}
              onClick={() => setViewMode('board')}
              title="Board View"
            >
              <LayoutGrid size={16} />
            </button>
          </div>
          
          {/* Filter button */}
          <button 
            className="flex items-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filters</span>
            <ChevronDown size={14} className={`transform transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Main content placeholder - would contain the full implementation */}
      <div className="flex-1 bg-slate-750 rounded-lg mx-4 mb-4 p-4 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-t-4 border-r-4 border-blue-500 rounded-full animate-spin mb-3"></div>
              <div className="text-slate-400">Loading trucks...</div>
            </div>
          </div>
        ) : sortedTrucks.length === 0 ? (
          <EmptyState />
        ) : (
          <div>
            <h2 className="text-white text-lg mb-4">Inbound Trucks Status Board</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-blue-400 text-sm font-semibold mb-2">Scheduled ({groupedTrucks.scheduled.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {scheduledByDate.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-3">No scheduled trucks</div>
                  ) : (
                    scheduledByDate.map(([dateLabel, dateGroup], groupIndex) => (
                      <div key={dateLabel}>
                        {/* Date separator */}
                        <div className={`sticky top-0 z-10 bg-slate-800 ${groupIndex > 0 ? 'mt-3' : ''}`}>
                          <div className="flex items-center py-2">
                            <div className="flex-1 h-px bg-slate-600"></div>
                            <span className={`px-3 text-xs font-medium ${
                              dateGroup.isToday ? 'text-yellow-400' : 'text-slate-400'
                            }`}>
                              {dateGroup.isToday ? 'Today' : new Date(dateGroup.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            <div className="flex-1 h-px bg-slate-600"></div>
                          </div>
                        </div>
                        
                        {/* Trucks for this date */}
                        <div className="space-y-2">
                          {dateGroup.trucks.map(truck => (
                            <TruckTile
                              key={truck.id}
                              truck={truck}
                              borderColor="border-slate-600"
                              statusBgColor="bg-blue-900/30"
                              statusTextColor="text-blue-300"
                              statusBorderColor="border-blue-500/20"
                              openTruckEditor={openTruckEditor}
                              onStatusChange={handleStatusChange}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-green-400 text-sm font-semibold mb-2">Arrived ({groupedTrucks.arrived.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.arrived.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-green-500/20"
                      statusBgColor="bg-green-900/30"
                      statusTextColor="text-green-300"
                      statusBorderColor="border-green-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                  {groupedTrucks.arrived.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No arrived trucks</div>
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-purple-400 text-sm font-semibold mb-2">In Progress ({groupedTrucks.inProgress.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.inProgress.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-purple-500/20"
                      statusBgColor="bg-purple-900/30"
                      statusTextColor="text-purple-300"
                      statusBorderColor="border-purple-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                  {groupedTrucks.inProgress.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No in-progress trucks</div>
                  )}
                </div>
              </div>
              <div className="bg-slate-800 p-3 rounded-lg">
                <h3 className="text-teal-400 text-sm font-semibold mb-2">Ready for Putaway ({groupedTrucks.completed.length})</h3>
                <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto pr-1">
                  {groupedTrucks.completed.map(truck => (
                    <TruckTile
                      key={truck.id}
                      truck={truck}
                      borderColor="border-teal-500/20"
                      statusBgColor="bg-teal-900/30"
                      statusTextColor="text-teal-300"
                      statusBorderColor="border-teal-500/20"
                      openTruckEditor={openTruckEditor}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                  {groupedTrucks.completed.length === 0 && (
                    <div className="text-slate-500 text-sm text-center py-3">No trucks ready for putaway</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivingView;